/**
 * About Us Team – GSAP ScrambleText on hero title, fade-in rows, member popup panel
 * @global gsap
 * @global ScrollTrigger
 * @global ScrambleTextPlugin
 */
(function () {
  const SECTION_SEL = '[data-about-us-team]';
  const TITLE_SEL = '[data-aut-hero-title]';
  const TAGLINE_SEL = '.about-us-team__hero-tagline';
  const ROW_SEL = '[data-aut-fade-row]';
  const CARD_SEL = '[data-aut-card]';
  const PLUS_SEL = '[data-aut-plus]';
  const PANEL_SEL = '[data-aut-panel]';
  const BACKDROP_SEL = '[data-aut-backdrop]';
  const PANEL_CLOSE_SEL = '[data-aut-panel-close]';

  function waitForGsap(cb) {
    const maxWait = 5000;
    const start = Date.now();
    function check() {
      if (typeof gsap !== 'undefined') {
        if (typeof ScrollTrigger !== 'undefined') gsap.registerPlugin(ScrollTrigger);
        if (typeof ScrambleTextPlugin !== 'undefined') gsap.registerPlugin(ScrambleTextPlugin);
        cb();
      } else if (Date.now() - start < maxWait) {
        requestAnimationFrame(check);
      } else {
        cb();
      }
    }
    check();
  }

  /** Returns true when the viewport is mobile-width (≤750px) — animations are off on mobile */
  function isMobile() {
    return window.innerWidth <= 750;
  }

  /** @param {HTMLElement} section */
  function initHeroScramble(section) {
    const title = section.querySelector(TITLE_SEL);
    if (!title) return;

    const tagline = section.querySelector(TAGLINE_SEL);
    const originalText = title.dataset.autOriginalText || (title.textContent || '').trim();
    if (!originalText) return;
    title.dataset.autOriginalText = originalText;

    const hasGsap = typeof gsap !== 'undefined';
    const hasScramble = hasGsap && typeof ScrambleTextPlugin !== 'undefined';
    const hasSplitText = hasGsap && typeof SplitText !== 'undefined';
    if (hasScramble) gsap.registerPlugin(ScrambleTextPlugin);
    if (hasSplitText) gsap.registerPlugin(SplitText);

    var mobile = isMobile();

    function playTitleAnimation() {
      if (hasGsap) {
        var naturalHeight = title.offsetHeight;
        var naturalTitleWidth = title.offsetWidth;
        gsap.set(title, { opacity: 0, minHeight: naturalHeight, width: mobile ? '' : '100vw' });
        if (tagline) gsap.set(tagline, { opacity: 0, willChange: 'opacity' });

        var taglineSplit = null;
        if (tagline && hasSplitText) {
          try {
            taglineSplit = new SplitText(tagline, { type: 'chars', specialChars: '.,!?:;+-=&/%$#@<>' });
            if (taglineSplit.chars && taglineSplit.chars.length) {
              gsap.set(taglineSplit.chars, { opacity: 0, yPercent: 100, willChange: 'transform, opacity' });
            }
          } catch (e) {
            taglineSplit = null;
          }
        }

        var tl = gsap.timeline({ defaults: { ease: 'power2.out' } });

        if (hasScramble && originalText) {
          tl.to(title, {
            duration: 1.2,
            opacity: 1,
            scrambleText: {
              text: originalText,
              chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
              revealDelay: 0.2,
              speed: 0.5
            },
            ease: 'none',
            onComplete: function () { gsap.set(title, { minHeight: '' }); }
          });
        } else {
          tl.fromTo(title, { opacity: 0, y: 24 }, {
            opacity: 1, y: 0, duration: 0.8, ease: 'expo.out',
            onComplete: function () { gsap.set(title, { minHeight: '' }); }
          });
        }

        // Step 2 — Title contracts to natural width + tagline reveals simultaneously
        tl.addLabel('reveal', '+=0.3');
        if (!mobile) {
          tl.to(title, { width: naturalTitleWidth, duration: 0.6, ease: 'power2.inOut', onComplete: function () { gsap.set(title, { width: '' }); } }, 'reveal');
        }

        if (taglineSplit && taglineSplit.chars && taglineSplit.chars.length) {
          tl.to(tagline, { opacity: 1, duration: 0.3 }, 'reveal');
          tl.to(taglineSplit.chars, { opacity: 1, yPercent: 0, duration: 0.3, stagger: 0.01, ease: 'expo.out' }, 'reveal');
        } else if (tagline) {
          tl.to(tagline, { opacity: 1, duration: 0.3 }, 'reveal');
        }
      } else {
        title.style.opacity = '0';
        title.style.transform = 'translateY(24px)';
        title.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
        requestAnimationFrame(() => {
          title.style.opacity = '1';
          title.style.transform = 'translateY(0)';
        });
      }
    }

    var heroWrap = section.querySelector('.about-us-team__hero-wrap') || section;
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            obs.unobserve(entry.target);
            playTitleAnimation();
          });
        },
        { threshold: 0.2 }
      );
      observer.observe(heroWrap);
    } else {
      playTitleAnimation();
    }
  }

  /** @param {HTMLElement} section */
  function initFadeRows(section) {
    const rows = section.querySelectorAll(ROW_SEL);
    if (!rows.length) return;

    // On mobile, skip all GSAP animations — ensure rows are fully visible
    if (isMobile()) {
      rows.forEach((row) => {
        /** @type {HTMLElement} */ (row).style.opacity = '1';
        /** @type {HTMLElement} */ (row).style.transform = 'none';
      });
      return;
    }

    if (typeof gsap !== 'undefined') {
      gsap.set(rows, { opacity: 0, y: 24 });
      if (typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
        gsap.to(rows, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.12,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 75%',
            toggleActions: 'play none none none'
          }
        });
      } else {
        gsap.to(rows, { opacity: 1, y: 0, duration: 0.6, stagger: 0.12, ease: 'power3.out' });
      }
    } else {
      rows.forEach((row, i) => {
        row.style.opacity = '0';
        row.style.transform = 'translateY(24px)';
        row.style.transition = `opacity 0.6s ease ${i * 0.12}s, transform 0.6s ease ${i * 0.12}s`;
      });
      const observer = typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
            (entries, obs) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                obs.unobserve(entry.target);
                rows.forEach((row) => {
                  row.style.opacity = '1';
                  row.style.transform = 'translateY(0)';
                });
              });
            },
            { threshold: 0.2 }
          )
        : null;
      if (observer) observer.observe(section);
      else rows.forEach((row) => {
        row.style.opacity = '1';
        row.style.transform = 'translateY(0)';
      });
    }
  }

  /** Plus button follows cursor inside the card image area (desktop hover devices only) */
  function initButtonFollow(section) {
    if (isMobile()) return;
    if (!window.matchMedia('(hover: hover)').matches) return;
    const cards = section.querySelectorAll(CARD_SEL);
    const BTN_HALF = 22;
    const PAD = 8;

    cards.forEach((card) => {
      const imageWrap = card.querySelector('.about-us-team__member-image-wrap');
      const action = card.querySelector('.about-us-team__card-action');
      if (!imageWrap || !action) return;

      card.addEventListener('mousemove', (e) => {
        const rect = imageWrap.getBoundingClientRect();
        let x = e.clientX - rect.left - BTN_HALF;
        let y = e.clientY - rect.top - BTN_HALF;
        x = Math.max(PAD, Math.min(rect.width - BTN_HALF * 2 - PAD, x));
        y = Math.max(PAD, Math.min(rect.height - BTN_HALF * 2 - PAD, y));
        action.style.left = x + 'px';
        action.style.top = y + 'px';
        action.style.right = 'auto';
        action.style.bottom = 'auto';
      });

      card.addEventListener('mouseleave', () => {
        action.style.left = '';
        action.style.top = '';
        action.style.right = '';
        action.style.bottom = '';
      });
    });
  }

  /** @param {HTMLElement} section */
  function initPanel(section) {
    const backdrop = section.querySelector(BACKDROP_SEL);
    const panels = section.querySelectorAll(PANEL_SEL);
    const closeBtns = section.querySelectorAll(PANEL_CLOSE_SEL);

    if (!backdrop || !panels.length) return;

    let activePanel = null;
    let escapeHandler = null;

    function getMemberName(el) {
      return (el.getAttribute('data-aut-member-name') || '').trim();
    }

    function removeEscapeHandler() {
      if (escapeHandler) {
        document.removeEventListener('keydown', escapeHandler);
        escapeHandler = null;
      }
    }

    function openPanel(card) {
      const memberName = getMemberName(card);
      if (!memberName) return;

      const panel = Array.from(panels).find((p) => getMemberName(p) === memberName);
      if (!panel) return;

      panels.forEach((p) => p.setAttribute('aria-hidden', 'true'));
      panel.setAttribute('aria-hidden', 'false');
      activePanel = panel;
      backdrop.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      if (typeof gsap !== 'undefined') {
        gsap.set(panel, { x: '100%' });
        gsap.set(backdrop, { opacity: 0 });
        gsap.to(backdrop, { opacity: 1, duration: 0.4, ease: 'power2.out' });
        gsap.to(panel, { x: 0, duration: 0.6, ease: 'power4.out' });
      } else {
        panel.style.transition = 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)';
        panel.style.transform = 'translateX(0)';
      }

      escapeHandler = (e) => {
        if (e.key === 'Escape') closePanel();
      };
      document.addEventListener('keydown', escapeHandler);
    }

    function closePanel() {
      if (!activePanel) return;
      removeEscapeHandler();
      const panel = activePanel;
      activePanel = null;

      if (typeof gsap !== 'undefined') {
        gsap.to(panel, {
          x: '100%',
          duration: 0.4,
          ease: 'power2.in',
          onComplete: () => {
            panel.setAttribute('aria-hidden', 'true');
            backdrop.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
          }
        });
        gsap.to(backdrop, { opacity: 0, duration: 0.3 });
      } else {
        panel.style.transition = 'transform 0.4s ease-in';
        panel.style.transform = 'translateX(100%)';
        let done = false;
        const finishClose = () => {
          if (done) return;
          done = true;
          panel.removeEventListener('transitionend', onTransitionEnd);
          panel.setAttribute('aria-hidden', 'true');
          backdrop.setAttribute('aria-hidden', 'true');
          document.body.style.overflow = '';
        };
        const onTransitionEnd = () => finishClose();
        panel.addEventListener('transitionend', onTransitionEnd);
        setTimeout(finishClose, 450);
      }
    }

    section.addEventListener('click', (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const card = target?.closest(CARD_SEL);
      if (!card) return;
      if (target?.closest(PLUS_SEL)) {
        e.stopPropagation();
        e.preventDefault();
        openPanel(card);
        return;
      }
      if (window.matchMedia('(hover: none)').matches) {
        e.preventDefault();
        openPanel(card);
      }
    });

    closeBtns.forEach((btn) => btn.addEventListener('click', closePanel));
    backdrop.addEventListener('click', closePanel);
  }

  /** @param {HTMLElement | null} section */
  function initSection(section) {
    if (!section || section.dataset.autInit === '1') return;
    section.dataset.autInit = '1';

    const enableAnimations = section.dataset.autAnimations === '1';
    const enablePopup = section.dataset.autPopup === '1';

    if (enableAnimations) {
      initHeroScramble(section);
      initFadeRows(section);
    }
    if (enablePopup) {
      initPanel(section);
      initButtonFollow(section);
    }
  }

  function init() {
    const sections = document.querySelectorAll(SECTION_SEL);
    sections.forEach((section) => initSection(section));
  }

  function run() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => waitForGsap(() => document.fonts.ready.then(init)));
    } else {
      waitForGsap(() => document.fonts.ready.then(init));
    }
  }
  run();

  document.addEventListener('shopify:section:load', (e) => {
    const target = e.target;
    if (target instanceof Element && target.matches(SECTION_SEL)) {
      delete target.dataset.autInit;
      initSection(target);
    } else if (target instanceof Element) {
      const section = target.querySelector(SECTION_SEL);
      if (section) {
        delete section.dataset.autInit;
        initSection(section);
      }
    }
  });
})();
